import { z } from "zod";
import { createClient } from "@supabase/supabase-js";
import { createServerFn } from "@tanstack/react-start";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import type { Database } from "@/integrations/supabase/types";
import { slugifyTag, findCanonicalTagBySlug } from "./tags";

function publicSupabase() {
  const url = process.env.SUPABASE_URL || process.env.VITE_SUPABASE_URL;
  const key = process.env.SUPABASE_PUBLISHABLE_KEY || process.env.VITE_SUPABASE_PUBLISHABLE_KEY;
  if (!url || !key) {
    throw new Error("Missing SUPABASE_URL / SUPABASE_PUBLISHABLE_KEY");
  }
  return createClient<Database>(url, key, {
    auth: { storage: undefined, persistSession: false, autoRefreshToken: false },
  });
}

const NullableText = (max: number) => z.string().trim().max(max).nullable().optional();
const SlugValue = z
  .string()
  .trim()
  .max(120)
  .regex(/^[a-z0-9-]*$/, "lowercase letters, numbers, hyphens only");

const ResourceInputSchema = z.object({
  id: z.string().uuid().optional(),
  slug: SlugValue.min(1),
  slug_es: SlugValue.nullable().optional(),
  title_en: z.string().trim().min(1).max(200),
  title_es: z.string().trim().min(1).max(200),
  meta_title_en: NullableText(200),
  meta_title_es: NullableText(200),
  excerpt_en: z.string().trim().max(300).default(""),
  excerpt_es: z.string().trim().max(300).default(""),
  body_en: z.string().max(100000).default(""),
  body_es: z.string().max(100000).default(""),
  cover_image_url: NullableText(500),
  cover_image_url_es: NullableText(500),
  tags_en: z.array(z.string().trim().min(1).max(60)).max(10).default([]),
  tags_es: z.array(z.string().trim().min(1).max(60)).max(10).default([]),
  reading_minutes: z.number().int().min(1).max(120).default(5),
  published: z.boolean().default(true),
  sort_order: z.number().int().default(0),
  featured: z.boolean().default(false),
  cluster: z
    .enum(["performance", "conversion", "migration", "technical-seo", "development", "general"])
    .nullable()
    .optional(),
  content_type: z
    .enum(["pillar", "tactical", "buyer_intent", "field_note", "case_note"])
    .nullable()
    .optional(),
  commercial_offer: z
    .enum(["performance_fix", "cro_technical_seo_fix", "custom_build_replatform"])
    .nullable()
    .optional(),
  primary_service: z
    .enum(["performance", "cro", "technical_seo", "migration", "development", "analytics"])
    .nullable()
    .optional(),
  pillar_slug: NullableText(160),
  review_status: z.enum(["draft", "needs_review", "reviewed", "evergreen"]).default("needs_review"),
  translation_status: z
    .enum(["complete", "needs_review", "missing", "planned"])
    .default("complete"),
  reviewed_at: NullableText(64),
  next_review_at: NullableText(64),
  cta_style: z.enum(["blue-glow", "navy-coral", "minimal-light", "none"]).nullable().optional(),
  cta_eyebrow_en: NullableText(120),
  cta_eyebrow_es: NullableText(120),
  cta_title_en: NullableText(200),
  cta_title_es: NullableText(200),
  cta_body_en: NullableText(600),
  cta_body_es: NullableText(600),
  cta_primary_label_en: NullableText(80),
  cta_primary_label_es: NullableText(80),
  cta_primary_href_en: NullableText(500),
  cta_primary_href_es: NullableText(500),
});

export type ResourceInput = z.infer<typeof ResourceInputSchema>;

const SlugSchema = z.object({ slug: SlugValue.min(1) });
const IdSchema = z.object({ id: z.string().uuid() });

function resourceTable(client: unknown): any {
  return (client as any).from("resources");
}

async function requireAdmin(context: any) {
  const { supabase, userId } = context;
  const { data: roleRow } = await supabase
    .from("user_roles")
    .select("role")
    .eq("user_id", userId)
    .eq("role", "admin")
    .maybeSingle();
  if (!roleRow) throw new Error("Forbidden");
}

const PUBLIC_LEGACY_FIELDS =
  "id, slug, slug_es, title_en, title_es, excerpt_en, excerpt_es, cover_image_url, cover_image_url_es, tags_en, tags_es, reading_minutes, sort_order, updated_at, featured";
const PUBLIC_TAXONOMY_FIELDS =
  `${PUBLIC_LEGACY_FIELDS}, reviewed_at, cluster, content_type, commercial_offer, primary_service, pillar_slug, review_status, translation_status, next_review_at`;

async function orderedPublicQuery(fields: string) {
  return resourceTable(publicSupabase())
    .select(fields)
    .eq("published", true)
    .order("sort_order", { ascending: true })
    .order("created_at", { ascending: false });
}

export const listPublicResources = createServerFn({ method: "GET" }).handler(async () => {
  try {
    const enriched = await orderedPublicQuery(PUBLIC_TAXONOMY_FIELDS);
    if (!enriched.error) return { resources: enriched.data ?? [], taxonomyAvailable: true };

    console.warn(
      "listPublicResources taxonomy query failed; retrying legacy public fields",
      enriched.error,
    );
    const legacy = await orderedPublicQuery(PUBLIC_LEGACY_FIELDS);
    if (legacy.error) {
      console.error("listPublicResources legacy fallback error", legacy.error);
      return { resources: [], taxonomyAvailable: false };
    }
    return { resources: legacy.data ?? [], taxonomyAvailable: false };
  } catch (error) {
    console.error("listPublicResources exception", error);
    return { resources: [], taxonomyAvailable: false };
  }
});

const RelatedSchema = z.object({
  slug: z.string().trim().min(1).max(120),
  tags: z.array(z.string().trim().max(80)).max(10).default([]),
  locale: z.enum(["en", "es"]).default("en"),
  limit: z.number().int().min(1).max(6).default(3),
  cluster: z
    .enum(["performance", "conversion", "migration", "technical-seo", "development", "general"])
    .nullable()
    .optional(),
});


const PublicResourcePageSchema = z.object({
  slug: SlugValue.min(1),
  locale: z.enum(["en", "es"]).default("en"),
  limit: z.number().int().min(1).max(6).default(3),
});

const PUBLIC_RELATED_FIELDS =
  "slug, slug_es, title_en, title_es, excerpt_en, excerpt_es, cover_image_url, cover_image_url_es, tags_en, tags_es, reading_minutes, sort_order, created_at";

function sharedTagCount(row: any, tags: string[], locale: "en" | "es"): number {
  if (tags.length === 0) return 0;
  const rowTags = ((locale === "es" ? row.tags_es : row.tags_en) ?? []) as string[];
  const wanted = new Set(tags.map((tag) => tag.trim().toLocaleLowerCase()).filter(Boolean));
  return rowTags.reduce(
    (score, tag) => score + (wanted.has(String(tag).trim().toLocaleLowerCase()) ? 1 : 0),
    0,
  );
}

/**
 * Page-level public read used by /resources/:slug and /es/recursos/:slug.
 *
 * The article and the lightweight related-resource index do not depend on one
 * another, so they are fetched concurrently. Ranking the related cards in the
 * worker removes the previous same-tag -> fallback query waterfall (up to
 * three sequential Supabase requests on a cold SSR render).
 */
export const getPublicResourcePage = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) => PublicResourcePageSchema.parse(input))
  .handler(async ({ data }) => {
    try {
      const client = publicSupabase();
      const [resourceResult, candidateResult] = await Promise.all([
        resourceTable(client)
          .select("*")
          .or(`slug.eq.${data.slug},slug_es.eq.${data.slug}`)
          .eq("published", true)
          .maybeSingle(),
        resourceTable(client)
          .select(PUBLIC_RELATED_FIELDS)
          .eq("published", true)
          .order("sort_order", { ascending: true })
          .order("created_at", { ascending: false })
          .limit(100),
      ]);

      if (resourceResult.error) {
        console.error("getPublicResourcePage resource error", resourceResult.error);
        return { resource: null, related: [] as any[] };
      }
      const resource = resourceResult.data;
      if (!resource) return { resource: null, related: [] as any[] };

      if (candidateResult.error) {
        console.error("getPublicResourcePage related error", candidateResult.error);
        return { resource, related: [] as any[] };
      }

      const tags = ((
        data.locale === "es"
          ? resource.tags_es ?? resource.tags_en
          : resource.tags_en
      ) ?? []) as string[];
      const currentSlugs = new Set(
        [resource.slug, resource.slug_es].filter((slug): slug is string => Boolean(slug)),
      );
      const related = (candidateResult.data ?? [])
        .filter((row: any) => !currentSlugs.has(row.slug) && !currentSlugs.has(row.slug_es))
        .map((row: any, index: number) => ({
          row,
          index,
          score: sharedTagCount(row, tags, data.locale),
        }))
        .sort(
          (
            a: { row: any; index: number; score: number },
            b: { row: any; index: number; score: number },
          ) => b.score - a.score || a.index - b.index,
        )
        .slice(0, data.limit)
        .map(({ row }: { row: any; index: number; score: number }) => row);

      return { resource, related };
    } catch (error) {
      console.error("getPublicResourcePage exception", error);
      return { resource: null, related: [] as any[] };
    }
  });

export const getRelatedResources = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) => RelatedSchema.parse(input))
  .handler(async ({ data }) => {
    try {
      const client = publicSupabase();
      const fields =
        "slug, slug_es, title_en, title_es, excerpt_en, excerpt_es, cover_image_url, cover_image_url_es, tags_en, tags_es, reading_minutes";
      const selected: any[] = [];
      const seen = new Set<string>([data.slug]);
      const addRows = (rows: any[] | null | undefined) => {
        for (const row of rows ?? []) {
          if (!row?.slug || seen.has(row.slug) || selected.length >= data.limit) continue;
          seen.add(row.slug);
          selected.push(row);
        }
      };

      if (data.tags.length > 0) {
        const tagColumn = data.locale === "es" ? "tags_es" : "tags_en";
        const sameTag = await resourceTable(client)
          .select(fields)
          .eq("published", true)
          .neq("slug", data.slug)
          .overlaps(tagColumn, data.tags)
          .order("sort_order", { ascending: true })
          .order("created_at", { ascending: false })
          .limit(data.limit * 2);
        if (sameTag.error) console.error("getRelatedResources sameTag error", sameTag.error);
        else addRows(sameTag.data);
      }

      if (selected.length < data.limit) {
        const fallback = await resourceTable(client)
          .select(fields)
          .eq("published", true)
          .neq("slug", data.slug)
          .order("sort_order", { ascending: true })
          .order("created_at", { ascending: false })
          .limit(data.limit * 2);
        if (fallback.error) console.error("getRelatedResources fallback error", fallback.error);
        else addRows(fallback.data);
      }

      return { related: selected.slice(0, data.limit) };
    } catch (error) {
      console.error("getRelatedResources exception", error);
      return { related: [] };
    }
  });

const TagSlugSchema = z.object({
  slug: z.string().trim().min(1).max(120),
  locale: z.enum(["en", "es"]).default("en"),
});

export const listResourcesByTag = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) => TagSlugSchema.parse(input))
  .handler(async ({ data }) => {
    try {
      const query = await orderedPublicQuery(PUBLIC_LEGACY_FIELDS);
      if (query.error) {
        console.error("listResourcesByTag error", query.error);
        return { resources: [] as any[], canonicalTag: null as string | null };
      }
      const all = query.data ?? [];
      const allTags: string[] = [];
      for (const row of all) {
        const tags = (data.locale === "es" ? row.tags_es : row.tags_en) ?? [];
        for (const tag of tags) allTags.push(tag);
      }
      const canonicalTag = findCanonicalTagBySlug(allTags, data.slug);
      const resources = all.filter((row: any) => {
        const tags = ((data.locale === "es" ? row.tags_es : row.tags_en) ?? []) as string[];
        return tags.some((tag) => slugifyTag(tag) === data.slug);
      });
      return { resources, canonicalTag };
    } catch (error) {
      console.error("listResourcesByTag exception", error);
      return { resources: [] as any[], canonicalTag: null as string | null };
    }
  });

export const listAllPublicTags = createServerFn({ method: "GET" }).handler(async () => {
  try {
    const { data, error } = await resourceTable(publicSupabase())
      .select("tags_en, tags_es")
      .eq("published", true);
    if (error) {
      console.error("listAllPublicTags error", error);
      return { en: [] as string[], es: [] as string[] };
    }
    const enSet = new Set<string>();
    const esSet = new Set<string>();
    for (const row of data ?? []) {
      for (const tag of (row.tags_en ?? []) as string[]) if (tag.trim()) enSet.add(tag.trim());
      for (const tag of (row.tags_es ?? []) as string[]) if (tag.trim()) esSet.add(tag.trim());
    }
    return {
      en: Array.from(enSet).sort((a, b) => a.localeCompare(b)),
      es: Array.from(esSet).sort((a, b) => a.localeCompare(b)),
    };
  } catch (error) {
    console.error("listAllPublicTags exception", error);
    return { en: [] as string[], es: [] as string[] };
  }
});

export const getPublicResource = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) => SlugSchema.parse(input))
  .handler(async ({ data }) => {
    const { data: row, error } = await resourceTable(publicSupabase())
      .select("*")
      .or(`slug.eq.${data.slug},slug_es.eq.${data.slug}`)
      .eq("published", true)
      .maybeSingle();
    if (error) {
      console.error("getPublicResource error", error);
      return { resource: null };
    }
    return { resource: row };
  });

export const listAdminResources = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await requireAdmin(context);
    const { data, error } = await resourceTable(supabaseAdmin)
      .select("*")
      .order("sort_order", { ascending: true });
    if (error) throw new Error(error.message);
    return { resources: data ?? [] };
  });

export const getResource = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => IdSchema.parse(input))
  .handler(async ({ data, context }) => {
    await requireAdmin(context);
    const { data: row, error } = await resourceTable(supabaseAdmin)
      .select("*")
      .eq("id", data.id)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return { resource: row };
  });

export const upsertResource = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => ResourceInputSchema.parse(input))
  .handler(async ({ data, context }) => {
    await requireAdmin(context);

    const payload = {
      slug: data.slug,
      slug_es: data.slug_es || null,
      title_en: data.title_en,
      title_es: data.title_es,
      meta_title_en: data.meta_title_en || null,
      meta_title_es: data.meta_title_es || null,
      excerpt_en: data.excerpt_en,
      excerpt_es: data.excerpt_es,
      body_en: data.body_en,
      body_es: data.body_es,
      cover_image_url: data.cover_image_url || null,
      cover_image_url_es: data.cover_image_url_es || null,
      tags_en: data.tags_en,
      tags_es: data.tags_es,
      reading_minutes: data.reading_minutes,
      published: data.published,
      sort_order: data.sort_order,
      featured: data.featured,
      cluster: data.cluster || null,
      content_type: data.content_type || null,
      commercial_offer: data.commercial_offer || null,
      primary_service: data.primary_service || null,
      pillar_slug: data.pillar_slug || null,
      review_status: data.review_status,
      translation_status: data.translation_status,
      reviewed_at: data.reviewed_at || null,
      next_review_at: data.next_review_at || null,
      cta_style: data.cta_style ?? null,
      cta_eyebrow_en: data.cta_eyebrow_en || null,
      cta_eyebrow_es: data.cta_eyebrow_es || null,
      cta_title_en: data.cta_title_en || null,
      cta_title_es: data.cta_title_es || null,
      cta_body_en: data.cta_body_en || null,
      cta_body_es: data.cta_body_es || null,
      cta_primary_label_en: data.cta_primary_label_en || null,
      cta_primary_label_es: data.cta_primary_label_es || null,
      cta_primary_href_en: data.cta_primary_href_en || null,
      cta_primary_href_es: data.cta_primary_href_es || null,
    };

    if (data.featured) {
      const query = resourceTable(supabaseAdmin).update({ featured: false }).eq("featured", true);
      const { error } = data.id ? await query.neq("id", data.id) : await query;
      if (error) throw new Error(error.message);
    }

    if (data.id) {
      const { data: row, error } = await resourceTable(supabaseAdmin)
        .update(payload)
        .eq("id", data.id)
        .select("id")
        .single();
      if (error) throw new Error(error.message);
      return { id: row.id as string };
    }

    const { data: row, error } = await resourceTable(supabaseAdmin)
      .insert(payload)
      .select("id")
      .single();
    if (error) throw new Error(error.message);
    return { id: row.id as string };
  });

export const deleteResource = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => IdSchema.parse(input))
  .handler(async ({ data, context }) => {
    await requireAdmin(context);
    const { error } = await resourceTable(supabaseAdmin).delete().eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

const ReorderSchema = z.object({
  items: z.array(z.object({ id: z.string().uuid(), sort_order: z.number().int() })).max(200),
});

export const reorderResources = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => ReorderSchema.parse(input))
  .handler(async ({ data, context }) => {
    await requireAdmin(context);
    for (const item of data.items) {
      const { error } = await resourceTable(supabaseAdmin)
        .update({ sort_order: item.sort_order })
        .eq("id", item.id);
      if (error) throw new Error(error.message);
    }
    return { ok: true };
  });

export const clearFeaturedResource = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await requireAdmin(context);
    const { error } = await resourceTable(supabaseAdmin)
      .update({ featured: false })
      .eq("featured", true);
    if (error) throw new Error(error.message);
    return { ok: true };
  });
